import React, { useRef, useState, useEffect } from 'react';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';

// Persist across VirtualKeyboard unmount/remount so layout language is not reset to English
// (e.g. after on-screen space, focus changes, or parent re-renders that recreate this subtree).
let virtualKeyboardLanguagePreference = 'english';

const VirtualKeyboard = ({ value, onChange, onClose, isVisible, fieldKey }) => {
  const keyboardRef = useRef(null);
  const suppressNextKeyboardChangeRef = useRef(false);
  const [layoutName, setLayoutName] = useState('default');
  const [language, setLanguage] = useState(() => virtualKeyboardLanguagePreference);

  // Sync keyboard display when value changes from external source
  useEffect(() => {
    if (keyboardRef.current && isVisible) {
      keyboardRef.current.setInput(value || '');
    }
  }, [value, isVisible, fieldKey, language]);

  const handleKeyboardChange = input => {
    if (suppressNextKeyboardChangeRef.current) {
      suppressNextKeyboardChangeRef.current = false;
      return;
    }
    onChange(input);
  };

  const handleKeyPress = button => {
    if (button === '{shift}' || button === '{lock}') {
      setLayoutName(layoutName === 'default' ? 'shift' : 'default');
      return;
    }

    if (button === '{enter}') {
      const currentValue = value || '';
      const normalizedFieldKey = typeof fieldKey === 'string' ? fieldKey.toLowerCase() : '';
      const isMultilineField =
        normalizedFieldKey === 'questionname' ||
        normalizedFieldKey === 'questiondescription' ||
        normalizedFieldKey === 'sectionname' ||
        normalizedFieldKey === 'sectiondescription' ||
        normalizedFieldKey === 'taskdescription' ||
        normalizedFieldKey.startsWith('choicename:');
      const enterToken = isMultilineField ? '\n' : ' ';
      const nextValue = `${currentValue}${enterToken}`;

      suppressNextKeyboardChangeRef.current = true;
      onChange(nextValue);
      keyboardRef.current?.setInput(nextValue);
      return false;
    }
  };

  const handleLanguageChange = (lang) => {
    virtualKeyboardLanguagePreference = lang;
    setLanguage(lang);
    setLayoutName('default');
  };

  const keyboardLayouts = {
    english: {
      default: [
        '` 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
        '{tab} q w e r t y u i o p [ ] \\',
        '{lock} a s d f g h j k l ; \' {enter}',
        '{shift} z x c v b n m , . / {shift}',
        '.com @ {space}'
      ],
      shift: [
        '~ ! @ # $ % ^ & * ( ) _ + {bksp}',
        '{tab} Q W E R T Y U I O P { } |',
        '{lock} A S D F G H J K L : " {enter}',
        '{shift} Z X C V B N M < > ? {shift}',
        '.com @ {space}'
      ]
    },
    hindi: {
      default: [
        '` १ २ ३ ४ ५ ६ ७ ८ ९ ० - ृ {bksp}',
        '{tab} ौ ै ा ी ू बृ ह ग द ज ड ़ ॉ',
        '{lock} ो े ् ि ु प र क त च ट {enter}',
        '{shift} ं म न व ल स , . य {shift}',
        '.com @ {space}'
      ],
      shift: [
        '~ ॅ ॆ ् र ज ञ त ृ ( ) ः ऋ {bksp}',
        '{tab} औ ऐ आ ई ऊ भ ङ घ ध झ ढ ञ ऑ',
        '{lock} ओ ए अ इ उ फ ऱ ख थ छ ठ {enter}',
        '{shift} ँ ण ऩ ळ श ष । ॥ ञ {shift}',
        '.com @ {space}'
      ]
    },
    tamil: {
      default: [
        '` 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
        '{tab} ஆ இ ஈ உ ஊ எ ஏ ஐ ஒ ஓ ஔ ்',
        '{lock} ா ி ீ ு ூ ெ ே ை ொ ோ ௌ {enter}',
        '{shift} க ங ச ஞ ட ண த ந ப ம ய {shift}',
        '.com @ {space}'
      ],
      shift: [
        '~ ! @ # $ % ^ & * ( ) _ + {bksp}',
        '{tab} அ ர ல வ ழ ள ற ன ஜ ஷ ஸ ஹ',
        '{lock} ஃ க் ங் ச் ஞ் ட் ண் த் ந் ப் ம் {enter}',
        '{shift} ய் ர் ல் வ் ழ் ள் ற் ன் ஶ ஸ் ஹ் {shift}',
        '.com @ {space}'
      ]
    },
    marathi: {
      default: [
        '` १ २ ३ ४ ५ ६ ७ ८ ९ ० - ृ {bksp}',
        '{tab} ौ ै ा ी ू बृ ह ग द ज ड ़ ॉ',
        '{lock} ो े ् ि ु प र क त च ट {enter}',
        '{shift} ं म न व ल स , . य {shift}',
        '.com @ {space}'
      ],
      shift: [
        '~ ॅ ॆ ् र ज ञ त ृ ( ) ः ऋ {bksp}',
        '{tab} औ ऐ आ ई ऊ भ ङ घ ध झ ढ ञ ऑ',
        '{lock} ओ ए अ इ उ फ ऱ ख थ छ ठ {enter}',
        '{shift} ँ ण ळ श ष क्ष ज्ञ त्र । ॥ {shift}',
        '.com @ {space}'
      ]
    },
    kannada: {
      default: [
        '` ೧ ೨ ೩ ೪ ೫ ೬ ೭ ೮ ೯ ೦ - ೃ {bksp}',
        '{tab} ೌ ೈ ಾ ೀ ೂ ಬೃ ಹ ಗ ದ ಜ ಡ ಼ ೊ',
        '{lock} ೋ ೇ ್ ಿ ು ಪ ರ ಕ ತ ಚ ಟ {enter}',
        '{shift} ಂ ಮ ನ ವ ಲ ಸ , . ಯ {shift}',
        '.com @ {space}'
      ],
      shift: [
        '~ ೱ ೲ ್ ರ ಜ ಞ ತ ೃ ( ) ಃ ಋ {bksp}',
        '{tab} ಔ ಐ ಆ ಈ ಊ ಭ ಙ ಘ ಧ ಝ ಢ ಞ ಒ',
        '{lock} ಓ ಏ ಅ ಇ ಉ ಫ ಱ ಖ ಥ ಛ ಠ {enter}',
        '{shift} ಁ ಣ ಳ ಶ ಷ ಕ್ಷ ಜ್ಞ । ॥ {shift}',
        '.com @ {space}'
      ]
    },
    telugu: {
      default: [
        '` ౧ ౨ ౩ ౪ ౫ ౬ ౭ ౮ ౯ ౦ - ృ {bksp}',
        '{tab} ౌ ై ా ీ ూ బృ హ గ ద జ డ ़ ొ',
        '{lock} ో ే ్ ి ు ప ర క త చ ట {enter}',
        '{shift} ం మ న వ ల స , . య {shift}',
        '.com @ {space}'
      ],
      shift: [
        '~ ౭ ౮ ్ ర జ ఞ త ృ ( ) ః ఋ {bksp}',
        '{tab} ఔ ఐ ఆ ఈ ఊ భ ఙ ఘ ధ ఝ ఢ ఞ ఒ',
        '{lock} ఓ ఏ అ ఇ ఉ ఫ ఱ ఖ థ ఛ ఠ {enter}',
        '{shift} ఁ ణ ళ శ ష క్ష జ్ఞ । ॥ {shift}',
        '.com @ {space}'
      ]
    }
  };

  if (!isVisible) return null;

  const languageButtons = [
    { code: 'english', label: 'English', font: 'Noto Sans' },
    { code: 'hindi', label: 'हिंदी', font: 'Noto Sans Devanagari' },
    { code: 'tamil', label: 'தமிழ்', font: 'Noto Sans Tamil' },
    { code: 'marathi', label: 'मराठी', font: 'Noto Sans Devanagari' },
    { code: 'kannada', label: 'ಕನ್ನಡ', font: 'Noto Sans Kannada' },
    { code: 'telugu', label: 'తెలుగు', font: 'Noto Sans Telugu' }
  ];

  return (
    <div style={{ marginTop: '10px', marginBottom: '20px', maxWidth: '100%', zIndex: 1000, position: 'relative', border: '2px solid #007bff', borderRadius: '8px', padding: '10px', backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
      <button
        type="button"
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          width: '30px',
          height: '30px',
          border: 'none',
          borderRadius: '50%',
          backgroundColor: '#ff4444',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={e => e.target.style.backgroundColor = '#cc0000'}
        onMouseLeave={e => e.target.style.backgroundColor = '#ff4444'}
        title="Close Keyboard"
      >
        ×
      </button>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px', marginTop: '30px' }}>
        {languageButtons.map(lang => (
          <button
            type="button"
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            onKeyDown={e => {
              // Avoid Space activating this button (and picking English) while using the on-screen keyboard.
              if (e.key === ' ' || e.key === 'Spacebar') e.preventDefault();
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: language === lang.code ? '#007bff' : '#ffffff',
              color: language === lang.code ? 'white' : 'black',
              border: language === lang.code ? 'none' : '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: lang.font,
              fontWeight: language === lang.code ? 'bold' : 'normal',
              boxShadow: language === lang.code ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
              transition: 'all 0.2s ease'
            }}
          >
            {lang.label}
          </button>
        ))}
      </div>
      <Keyboard
        key={`${fieldKey}-${language}`}
        keyboardRef={r => (keyboardRef.current = r)}
        layoutName={layoutName}
        layout={keyboardLayouts[language]}
        onChange={handleKeyboardChange}
        onKeyPress={handleKeyPress}
        theme="hg-theme-default hg-layout-default"
        input={value || ''}
        display={{
          '{bksp}': '⌫ backspace',
          '{enter}': '↵ enter',
          '{shift}': '⇧ shift',
          '{space}': 'space',
          '{tab}': '⇥ tab',
          '{lock}': '⇪ caps'
        }}
      />
    </div>
  );
};

export default VirtualKeyboard;
